import { lazy, ComponentType } from 'react';

export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  chunkName?: string
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const maxRetries = 3;
    const storageKey = `chunk_refresh_${chunkName || 'page'}`;
    const hasAlreadyRefreshed = sessionStorage.getItem(storageKey) === 'true';
    let lastError: Error | null = null;

    // Try loading with retries and delays
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const component = await componentImport();
        // Success - clear any refresh flags
        sessionStorage.removeItem(storageKey);
        return component;
      } catch (error) {
        lastError = error as Error;
        console.warn(`Chunk load attempt ${attempt + 1}/${maxRetries} failed for ${chunkName}:`, error);
        
        // Wait before retrying (allows CDN propagation)
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
        }
      }
    }

    // All retries failed
    console.error(`All ${maxRetries} attempts failed for ${chunkName}:`, lastError);

    if (!hasAlreadyRefreshed) {
      // Set flag before refreshing to prevent infinite loop
      sessionStorage.setItem(storageKey, 'true');
      
      // Simple hard reload - let browser fetch fresh resources
      window.location.reload();
      
      // Return empty component while reloading
      return { default: (() => null) as unknown as T };
    }

    // Already tried refreshing, let ErrorBoundary handle it
    throw lastError;
  });
}
